import {
  GeckoAssetModel,
  GeckoAssetModelType,
  GeckoComponentModel,
  GeckoComponentModelType,
  GeckoMeasurement,
} from 'types'

/**
 * Computes the diameter and length of an asset based on its component models.
 * Returns raw measurements in their original units - callers should convert to desired units.
 *
 * @param assetModel - The asset model containing asset type information
 * @param componentModels - Array of component models to analyze
 * @returns Object containing diameter and length as GeckoMeasurement objects, or undefined if not available
 */
export const computeAssetDimensions = (
  assetModel: GeckoAssetModel | undefined,
  componentModels: readonly GeckoComponentModel[]
): { diameter?: GeckoMeasurement; length?: GeckoMeasurement } => {
  if (!assetModel || componentModels.length === 0) {
    return { diameter: undefined, length: undefined }
  }

  const assetType = assetModel.data.type

  switch (assetType) {
    case GeckoAssetModelType.Piping: {
      let diameter: GeckoMeasurement | undefined
      let totalLength: GeckoMeasurement | undefined

      // GeckoComponentModel is a union of different component types (e.g. PipeSection, PipeBend).
      // By intersecting with optional properties, TypeScript allows access to radius/length on components
      // that actually have these properties. At runtime, if a specific component has radius or length
      // they'll be accessible; if not, they'll be undefined.
      for (const component of componentModels as (GeckoComponentModel & {
        radius?: GeckoMeasurement
        length?: GeckoMeasurement
      })[]) {
        if (component.type === GeckoComponentModelType.PipeSection) {
          if (component.radius && !diameter) {
            diameter = component.radius.scale(2)
          }

          if (component.length) {
            totalLength = totalLength
              ? totalLength.plus(component.length)
              : component.length
          }
        }
      }

      return {
        diameter,
        length: totalLength,
      }
    }

    case GeckoAssetModelType.PressureVessel: {
      let diameter: GeckoMeasurement | undefined
      let totalLength: GeckoMeasurement | undefined

      for (const component of componentModels as (GeckoComponentModel & {
        radius?: GeckoMeasurement
        minorRadius?: GeckoMeasurement
        length?: GeckoMeasurement
      })[]) {
        switch (component.type) {
          case GeckoComponentModelType.PressureVesselShell:
            if (component.radius && !diameter) {
              diameter = component.radius.scale(2)
            }
            if (component.length) {
              totalLength = totalLength
                ? totalLength.plus(component.length)
                : component.length
            }
            break

          case GeckoComponentModelType.HemisphericalHead:
            if (component.radius) {
              totalLength = totalLength
                ? totalLength.plus(component.radius)
                : component.radius
            }
            break

          case GeckoComponentModelType.EllipsoidalHead:
            if (component.minorRadius) {
              totalLength = totalLength
                ? totalLength.plus(component.minorRadius)
                : component.minorRadius
            }
            break
        }
      }

      return {
        diameter,
        length: totalLength,
      }
    }

    default: {
      let diameter: GeckoMeasurement | undefined
      let length: GeckoMeasurement | undefined

      for (const component of componentModels) {
        const componentWithProps = component as GeckoComponentModel & {
          radius?: GeckoMeasurement
          length?: GeckoMeasurement
        }

        if (componentWithProps.radius && !diameter) {
          diameter = componentWithProps.radius.scale(2)
        }

        if (componentWithProps.length && !length) {
          length = componentWithProps.length
        }
      }

      return { diameter, length }
    }
  }
}
